import React, { useEffect, useMemo, useRef } from "react";
import qs from "query-string";
import { useLocation } from "react-router-dom";
import { isSafari, isMobileSafari, isTablet, isIOS } from "react-device-detect";

import { useAppDispatch, useAppSelector } from "@/redux/store";
import { withPageInitialLoad } from "@/libs/pageDataBridge";
import withAuthenticationChecking from "../../hocs/withAuthenticationChecking";

import { LOADER_TYPES, REDIRECTION_PAGES } from "../../constants";
import { getPageApi } from "../../redux/actions/page.action";
import { hideErrorPage } from "../../redux/actions/common.action";
import CatalogPage from "../catalogPage";
import ErrorPage from "../errorPage";
import ProductListing from "../productListing";
import { dynamicPageSlice } from "@/redux/features";
import CommonAnalyticsEvent from "@/services/analytics/main/common";
import { SaleWrapper } from "@/components/dynamic/dynamic-sale";
import { DynamicYieldService } from "@/services";
import { CatalogDataProps } from "../catalogPage/types";
import { ProductsListingPageData } from "../productListing/types";
import { SearchType } from "@/services/analytics/main/common/types";

// Common static asset file extensions that should not trigger get-page API calls
const STATIC_ASSET_EXTENSIONS = [
  ".css",
  ".js",
  ".ts",
  ".tsx",
  ".jsx",
  ".png",
  ".jpg",
  ".jpeg",
  ".gif",
  ".svg",
  ".woff",
  ".woff2",
  ".ttf",
  ".eot",
  ".ico",
  ".webp",
  ".json",
  ".map",
  ".xml",
  ".txt",
  ".pdf",
  ".env",
  ".php"
];

// Common static asset path prefixes (for files without extensions like /media/qrcode-app)
const STATIC_ASSET_PATH_PREFIXES = [
  "/static/",
  "/media/",
  "/locales/",
  "/favicon",
  "/robots.txt",
  "/sitemap",
  "/.well-known/"
];

const isStaticAsset = (pathname: string): boolean => {
  const lowerPath = pathname.toLowerCase();

  // Check for static asset path prefixes anywhere in the path
  // This handles cases like /en/media/qrcode-app or /en-ae/static/css/main.css
  // Using includes() is safe here because these prefixes are specific to static assets
  // and unlikely to appear in actual page URLs
  if (STATIC_ASSET_PATH_PREFIXES.some(prefix => lowerPath.includes(prefix))) {
    return true;
  }

  // Check for static asset file extensions
  return STATIC_ASSET_EXTENSIONS.some(ext => lowerPath.endsWith(ext));
};

const DynamicPage = () => {
  const pageData = useAppSelector(state => state.dynamicPage.data);
  const errorPage = useAppSelector(state => state.common.errorPage);
  const dispatch = useAppDispatch();
  const location = useLocation<
    Location & {
      fromSearch: boolean;
      searchPayload: {
        searchTerm: string;
        searchCategory: string;
        searchType: SearchType;
      };
    }
  >();
  const isIpadOnSafari = isTablet && isIOS && (isSafari || isMobileSafari);

  // Track previous pathname to detect actual navigation (not initial mount/refresh)
  const prevPathnameRef = useRef<string | null>(null);
  const dyScriptRef = useRef<HTMLScriptElement | null>(null);

  // Check if there's an error state (404, 500, 503)
  // Only show error if we have error state AND no valid page data
  // This prevents showing error page when navigating to a valid page that's still loading
  const hasError =
    (errorPage.is404 || errorPage.is500 || errorPage.is503) && !pageData?.type;

  const contentToDisplay = useMemo(() => {
    // Show error page if there's an error state and no valid page data
    if (hasError) {
      return <ErrorPage />;
    }

    switch (pageData?.type) {
      case REDIRECTION_PAGES.catalog:
        return (
          <CatalogPage
            data={pageData.data as CatalogDataProps}
            meta={pageData.meta}
            contentSettings={{
              countryId: pageData.countryId,
              language: pageData.language
            }}
            subtype={pageData.subtype}
          />
        );

      case REDIRECTION_PAGES.productListing:
        return (
          <ProductListing
            providedListingData={pageData.data as ProductsListingPageData}
            metaData={pageData.meta}
            breadcrumbs={pageData.breadcrumbs}
            plpSections={pageData.cms}
            subtype={pageData.subtype}
          />
        );

      case REDIRECTION_PAGES.flashSale:
        return <SaleWrapper pageData={pageData as any} />;

      default: {
        return pageData?.data ? <ErrorPage /> : null;
      }
    }
  }, [pageData, hasError]);

  useEffect(() => {
    // Only reset page data and clear error state when actually navigating to a different page
    // Don't reset on initial mount/refresh to preserve SSR data
    const currentPathname = location.pathname;
    const prevPathname = prevPathnameRef.current;

    // If pathname changed (actual navigation, not initial mount)
    if (prevPathname !== null && prevPathname !== currentPathname) {
      // Clear error state and reset page data when navigating to a new page
      // This prevents the error page from persisting when navigating to valid pages
      dispatch(hideErrorPage());
      // Reset page data to ensure we don't show stale data from previous page
      dispatch(dynamicPageSlice.actions.resetDynamicPageData());
    }

    // Update the ref for next render
    prevPathnameRef.current = currentPathname;

    return () => {
      if (!isIpadOnSafari) {
        dispatch(dynamicPageSlice.actions.resetDynamicPageData());
      }
    };
  }, [location.pathname, dispatch, isIpadOnSafari]);

  useEffect(() => {
    if (location.state?.fromSearch && pageData.data) {
      const { searchPayload } = location.state;
      const nbHits = (pageData.data as ProductsListingPageData).nbHits;
      if (nbHits) {
        CommonAnalyticsEvent.viewSearchResult({
          ...searchPayload,
          searchResultCount: nbHits
        });
      } else {
        const { searchTerm } = searchPayload;
        CommonAnalyticsEvent.emptySearchResult({
          searchText: searchTerm
        });
      }
    }
  }, [pageData]);

  useEffect(() => {
    if (pageData.subtype) {
      let categories = undefined;
      const breadcrumbs = pageData.breadcrumbs;

      if (breadcrumbs) {
        categories = breadcrumbs.map(breadcrumb => breadcrumb.defaultValue);
      }
      dyScriptRef.current = DynamicYieldService.setDYHead(
        pageData.subtype,
        pageData.language,
        categories
      );
    }
  }, [pageData]);

  useEffect(() => {
    return () => {
      dyScriptRef.current?.remove();
    };
  }, []);

  return <div>{contentToDisplay}</div>;
};

export default withPageInitialLoad({
  pageName: "",
  page: withAuthenticationChecking(DynamicPage),
  strategy: async ({ store, params, query, language, url, ipcountry }) => {
    // Clear any previous error state at the start of loading a new page
    // This ensures error state from previous pages doesn't persist
    store.dispatch(hideErrorPage());

    // Skip get-page API call for static asset files to prevent unnecessary API requests
    if (isStaticAsset(url)) {
      return;
    }

    const countryId = store.getState().common.settings.countryId;

    await store.dispatch(
      getPageApi({
        url: qs.stringifyUrl(
          { url: url.replace(`/${params.language}/`, "/"), query },
          { skipNull: true, encode: false }
        ),
        language,
        countryId,
        ipcountry
      })
    );
  },
  ssr: true,
  loaderConfig: {
    type: LOADER_TYPES.DOTS,
    fullscreen: true,
    size: 16,
    qty: 3,
    background: "rgba(251, 251, 253, 0.4)"
  }
});
